import React, { memo, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/migration/chevron-right';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack } from '@atlaskit/primitives';

import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { ErrorBoundary } from '../common/ui/error-boundary';
import { useTenantContext } from '../common/utils';
import { KnowledgeBadge } from '../knowledge-card-components';
import { RelatedEntityType } from '../related-entities-api';

import {
	onRelatedEntityClick,
	onSurfaceShown,
	onToggleMoreRelatedEntitiesClick,
} from './analytics';
import { messages } from './messages';
import { useTopicBreakElems } from './set-break-point';
import { useRelatedTopics } from './use-related-topics';

export type AITopicsByPersonProps = {
	userId: string;
	userName: string;
	cloudId: string;
	sessionId: string;
	v2styles?: boolean;
};

const AITopicsByPersonComponent = memo(
	({ userId, userName, cloudId, sessionId, v2styles = false }: AITopicsByPersonProps) => {
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { commonAttributes } = useAIAnswerContext();
		const { aiRelatedTopics, fetchRelatedTopics, showTopics } = useRelatedTopics();
		const wrapperRef = useRef<HTMLDivElement | null>(null);
		const [showMore, setShowMore] = useState<boolean>(false);
		const { breakElems } = useTopicBreakElems(wrapperRef, showTopics);
		const { tenantContext } = useTenantContext(cloudId);

		const { cloudUrl } = tenantContext;

		const onBadgeClickHandler = ({
			topicId,
			position,
			shownAfterCardBodyExpanded,
		}: {
			topicId: string;
			position: number;
			shownAfterCardBodyExpanded: boolean;
		}) => {
			fireAnalyticsEvent(
				onRelatedEntityClick({
					clickedEntityId: topicId,
					clickedEntityType: 'topic',
					principalEntityType: RelatedEntityType.USER,
					principalEntityId: userId,
					sessionId,
					location: 'personCard',
					position,
					shownAfterCardBodyExpanded,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.TOPIC_CLICKED, commonAttributes });
		};

		const onShowMoreTopicsClick = () => {
			fireAnalyticsEvent(
				onToggleMoreRelatedEntitiesClick({
					sessionId,
					location: 'personCard',
					expanded: !showMore,
				}),
			);
			if (!showMore) {
				fireSAINUsageEvent({ metCriteria: SainAction.SHOW_MORE_TOPICS_CLICKED, commonAttributes });
			}
			setShowMore((s) => !s);
		};

		useEffect(() => {
			fetchRelatedTopics({
				entityId: userId,
				principalEntityType: RelatedEntityType.USER,
				cloudId,
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		useEffect(() => {
			if (showTopics) {
				fireAnalyticsEvent(
					onSurfaceShown({
						principalEntityId: userId,
						principalEntityType: RelatedEntityType.USER,
						relatedTopics: aiRelatedTopics.map((topic) => topic.id),
						sessionId,
						location: 'personCard',
					}),
				);
			}
		}, [showTopics, aiRelatedTopics, userId, fireAnalyticsEvent, sessionId]);

		const firstName = userName.split(' ')[0];

		return showTopics ? (
			v2styles ? (
				<Box>
					<Stack space="space.075">
						<Inline
							ref={wrapperRef}
							space="space.100"
							rowSpace="space.100"
							alignBlock="center"
							shouldWrap
						>
							{(breakElems < 0
								? aiRelatedTopics
								: aiRelatedTopics.slice(0, Math.max(1, breakElems))
							).map((topic, position) => (
								<KnowledgeBadge
									key={topic.id}
									name={topic.name}
									url={`/wiki/topics/${topic.id}`}
									onBadgeClick={() =>
										onBadgeClickHandler({
											topicId: topic.id,
											position,
											shownAfterCardBodyExpanded: false,
										})
									}
								/>
							))}
							{breakElems < aiRelatedTopics.length ? (
								<IconButton
									appearance="subtle"
									onClick={() =>
										fg('kd_fix_sain_mau_instrumentation')
											? onShowMoreTopicsClick()
											: setShowMore((s) => !s)
									}
									icon={(iconProps) =>
										showMore ? (
											<ChevronDownIcon {...iconProps} size="small" />
										) : (
											<ChevronRightIcon {...iconProps} size="small" />
										)
									}
									label={
										showMore ? (
											<FormattedMessage {...messages.ai_topics_show_fewer_topics_text} />
										) : (
											<FormattedMessage {...messages.ai_topics_show_more_topics_text} />
										)
									}
									spacing="compact"
								/>
							) : null}
						</Inline>
						{showMore && (
							<Box paddingBlockStart="space.025">
								<Inline space="space.100" shouldWrap>
									{aiRelatedTopics.slice(breakElems).map((topic, position) => (
										<KnowledgeBadge
											key={topic.id}
											name={topic.name}
											url={cloudUrl ? `${cloudUrl}/wiki/topics/${topic.id}` : undefined}
											onBadgeClick={() =>
												onBadgeClickHandler({
													topicId: topic.id,
													position: breakElems + position,
													shownAfterCardBodyExpanded: true,
												})
											}
										/>
									))}
								</Inline>
							</Box>
						)}
					</Stack>
				</Box>
			) : (
				<Box paddingBlockStart="space.200">
					<Stack space="space.100">
						<Heading size="xxsmall">
							<FormattedMessage
								{...messages.ai_topics_surface_header_person}
								values={{ userName: firstName }}
							/>
						</Heading>
						<Inline space="space.100" rowSpace="space.100" shouldWrap>
							{aiRelatedTopics.map((topic, position) => (
								<KnowledgeBadge
									key={topic.id}
									name={topic.name}
									url={cloudUrl ? `${cloudUrl}/wiki/topics/${topic.id}` : undefined}
									onBadgeClick={() =>
										onBadgeClickHandler({
											topicId: topic.id,
											position,
											shownAfterCardBodyExpanded: false,
										})
									}
								/>
							))}
						</Inline>
					</Stack>
				</Box>
			)
		) : null;
	},
);

export const AITopicsByPerson = (props: AITopicsByPersonProps): React.JSX.Element => (
	<ErrorBoundary>
		<AITopicsByPersonComponent {...props} />
	</ErrorBoundary>
);
